/**
 * Geo maths — one implementation, used by both halves.
 *
 * `places.ts` uses this to turn "near me" into a query D1 can actually run;
 * `Map.tsx` uses it to turn a list of markers into the box Leaflet should fit
 * to. Writing haversine once in the API and again in the client is how the two
 * quietly disagree about how far apart two points are — a bug that shows up as
 * "the map says 2km, the list says 2.3km" and takes an hour to even notice,
 * because neither number looks obviously wrong on its own.
 *
 * ── Why this lives in src/lib and not shared/ ────────────────────────────────
 *
 * `shared/` (see shared/time.ts) is the established home for code both halves
 * import, and services/api/tsconfig.json already lists it in `include` for
 * exactly that reason. This file is not there — the module it belongs to was
 * specified with it at src/lib/geo.ts — so services/api/src/places.ts reaches
 * it with a relative import that crosses out of services/api entirely
 * (`../../../src/lib/geo`).
 *
 * That import is safe to leave as a plain relative path rather than promoted
 * into shared/: everything below is pure arithmetic — no DOM, no Node
 * built-in, no Workers-only API — so it behaves identically in a browser, in
 * workerd, and under plain Node. esbuild (what Wrangler and
 * vitest-pool-workers both bundle with) resolves a relative path by walking
 * the filesystem, not by consulting a tsconfig's `include`, so the file
 * bundles into the Worker exactly as it would if it lived in shared/. If this
 * module grows a THIRD consumer, that is the moment to actually move it into
 * shared/ and fix the one import in places.ts — not before.
 */

export interface LatLng {
  lat: number
  lng: number
}

export interface BoundingBox {
  minLat: number
  maxLat: number
  minLng: number
  maxLng: number
}

/**
 * Range-valid, and nothing more.
 *
 * This is the one check both a client form and the server write path run —
 * rule 14 in CLAUDE.md: a rule the UI enforces and the server does not is not
 * enforced, so both call this rather than each keeping its own copy of
 * -90..90/-180..180.
 *
 * It catches a coordinate that cannot exist — a latitude past the pole, a
 * longitude off the far side of the planet. It also catches a SWAPPED
 * lat/lng, but only when the original longitude exceeds 90° in magnitude — a
 * latitude tops out at 90, so it is always a legal longitude too, and a swap
 * only fails once the number moving into the latitude slot was too big to be
 * one. Vieques sits close to (18.13, -65.42): swapped, that becomes
 * (-65.42, 18.13), and BOTH halves are still in range, because 65.42 does not
 * exceed 90 — this does NOT catch that swap. It catches one only for a place
 * more than 90° of longitude from the prime meridian, which rules out Puerto
 * Rico, Detroit, and most of the Americas east of the Rockies. No range check
 * is proof a coordinate points at the right place, only that it could exist
 * somewhere — and for this template's own places, even that proof is weaker
 * than it looks.
 */
export function isValidLatLng(point: LatLng): boolean {
  return (
    Number.isFinite(point.lat) && Number.isFinite(point.lng) &&
    point.lat >= -90 && point.lat <= 90 &&
    point.lng >= -180 && point.lng <= 180
  )
}

const EARTH_RADIUS_KM = 6371

/**
 * Great-circle distance, in kilometres.
 *
 * Haversine, not the flat-plane Pythagoras shortcut some map code uses to
 * save a few trig calls — that shortcut is fine within a city block and
 * measurably wrong across an island-length distance, which is the scale a
 * "how far is the ferry terminal" answer actually needs to be right at.
 */
export function haversineKm(a: LatLng, b: LatLng): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const sinLat = Math.sin(dLat / 2)
  const sinLng = Math.sin(dLng / 2)
  const h = sinLat * sinLat + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLng * sinLng
  // Clamped: floating-point error can push h fractionally past 1 for two
  // points that are (almost) antipodal, and Math.asin of anything past 1 is
  // NaN — a silent poison that only surfaces several calculations later, as
  // a sort that mysteriously puts everything after the NaN in the wrong order.
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)))
}

const KM_PER_DEGREE_LAT = 111.32

/**
 * A box that CONTAINS every point within `radiusKm` of `center` — not the
 * circle itself, and that difference is the whole design. A box is cheap:
 * `lat BETWEEN ? AND ? AND lng BETWEEN ? AND ?` is an ordinary indexed range
 * scan, where a true radius query would need either a spatial index D1 does
 * not offer or a haversineKm call against every row in the table. So the box
 * is the SQL pre-filter — "near me" in places.ts is this box query followed
 * by a JS pass that measures the (small) result with haversineKm, drops the
 * corners the box over-includes, and sorts by actual distance.
 *
 * Degrees of latitude are ~constant everywhere; degrees of longitude shrink
 * toward the poles by cos(latitude), which is why the box narrows in
 * longitude as `center` moves away from the equator. `cosLat` is clamped away
 * from exactly zero so a place plotted at the pole itself does not divide by
 * (nearly) nothing and produce a box wide enough to wrap the planet.
 *
 * Does not handle the antimeridian (±180°) — every Studio42 deployment so far
 * is somewhere in the Americas, nowhere near it. A project that changes that
 * will need the SQL query in places.ts split into two ranges when
 * minLng/maxLng cross ±180, which this function does not attempt.
 */
export function boundingBoxAround(center: LatLng, radiusKm: number): BoundingBox {
  const latDelta = radiusKm / KM_PER_DEGREE_LAT
  const cosLat = Math.max(Math.cos((center.lat * Math.PI) / 180), 0.000001)
  const lngDelta = radiusKm / (KM_PER_DEGREE_LAT * cosLat)
  return {
    minLat: Math.max(center.lat - latDelta, -90),
    maxLat: Math.min(center.lat + latDelta, 90),
    minLng: center.lng - lngDelta,
    maxLng: center.lng + lngDelta,
  }
}

/**
 * The box that contains every point given — what Map.tsx fits the view to
 * after markers change, and a reasonable "what is on this screen" default
 * before anyone has panned. `null` for an empty list rather than a box around
 * (0, 0): the Gulf of Guinea is not a sensible default view for an
 * application with no markers yet.
 */
export function boundsOf(points: LatLng[]): BoundingBox | null {
  if (points.length === 0) return null
  let minLat = 90
  let maxLat = -90
  let minLng = 180
  let maxLng = -180
  for (const p of points) {
    if (p.lat < minLat) minLat = p.lat
    if (p.lat > maxLat) maxLat = p.lat
    if (p.lng < minLng) minLng = p.lng
    if (p.lng > maxLng) maxLng = p.lng
  }
  return { minLat, maxLat, minLng, maxLng }
}
